import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { AgentTypes } from '../constants/event';
import { selectFeatureFlagUnresolvedItems } from '../redux/features/config/config.selector';
import {
  invalidOptionAnchorKey,
  noDefaultOptionAnchorKey,
  notOnMenuAnchorKey,
  quantityEscalationAnchorKey,
  unmatchedCouponAnchorKey,
} from '../redux/features/unresolvedItems/unresolvedItems.constants';
import { IUnresolvedItemAnchor } from '../redux/features/unresolvedItems/unresolvedItems.props';
import {
  selectAllInvalidOptionLines,
  selectAllNoDefaultOptionConstraints,
  selectAllNotOnMenuCards,
  selectAllQuantityEscalationItems,
  selectAllUnmatchedCoupons,
  selectUnresolvedItemAnchors,
} from '../redux/features/unresolvedItems/unresolvedItems.selector';
import { captureAnchors } from '../redux/features/unresolvedItems/unresolvedItems.slice';
import {
  selectCurrentSessionId,
  selectHypothesisFrame,
  selectTranscriptMessages,
} from '../selectors/message';
import { ISOStringToEpoch } from '../utils/network';

/** No usable trigger line — the card renders without a quote. */
const EMPTY_ANCHOR: IUnresolvedItemAnchor = { utterance: '', messageId: '' };

/**
 * The committed customer lines of the rendered transcript — the same gates
 * MessageContainer renders by (ORDERBOARD + non-TTS + non-empty payload), minus
 * the in-progress hypothesis, which is not a committed line. Exported for tests.
 */
const collectCommittedCustomerLines = (transcriptMessages: any[]): any[] =>
  transcriptMessages.filter((frame: any) => {
    if (frame?.agent_type !== AgentTypes.orderBoard) return false;
    const status = frame?.data?.status;
    if (status === 'TTS' || status === 'hypothesis') return false;
    const payload = frame?.data?.payload;
    return typeof payload === 'string' && payload.trim() !== '';
  });

/**
 * Pick the anchor for cards that first appeared in the current hypothesis
 * frame — the newest committed customer line — UNLESS the attribution is
 * ambiguous: more than one customer line landed since the previous hypothesis
 * frame (or, with no previous frame to bound the window, more than one line
 * exists at all). A wrong quote is worse than no quote, so ambiguity yields the
 * empty anchor, which first-wins into the store and permanently renders the
 * card quote-less (review round 2). Exported for tests.
 */
const resolveAnchor = (
  customerLines: any[],
  prevFrameEpoch: number | null
): IUnresolvedItemAnchor => {
  if (customerLines.length === 0) return EMPTY_ANCHOR;
  const candidates =
    prevFrameEpoch === null
      ? customerLines
      : customerLines.filter(
          (frame: any) => ISOStringToEpoch(frame?.timestamp) > prevFrameEpoch
        );
  if (candidates.length > 1) return EMPTY_ANCHOR;
  const newest = customerLines[customerLines.length - 1];
  return {
    utterance: newest.data.payload,
    messageId: newest.id,
    timestamp: newest.timestamp,
  };
};

/**
 * HITL-1621: pin each unresolved item card to the customer utterance that
 * triggered it.
 *
 * The producer re-sends the cumulative lists on every hypothesis frame, and the
 * frame where a card FIRST appears lands right after the triggering utterance
 * (2.6s median in production). So when a card key shows up that has no anchor
 * yet, the newest customer line on the rendered transcript is that card's
 * trigger — captured once, first write wins (see the slice), and suppressed
 * when several lines landed inside the attribution window (see resolveAnchor).
 *
 * Reads the same rendered-transcript list the UI paints (secondary-ASR source
 * swap included), so the anchor's messageId is always a line that exists on
 * screen.
 */
const useUnresolvedItemAnchors = () => {
  const dispatch = useAppDispatch();
  const enabled = useAppSelector(selectFeatureFlagUnresolvedItems);
  const anchors = useAppSelector(selectUnresolvedItemAnchors);
  const hypothesisFrame = useAppSelector(selectHypothesisFrame);
  const currentSessionId = useAppSelector(selectCurrentSessionId);
  const notOnMenuCards = useAppSelector(selectAllNotOnMenuCards);
  const invalidOptionLines = useAppSelector(selectAllInvalidOptionLines);
  const quantityEscalationItems = useAppSelector(
    selectAllQuantityEscalationItems
  );
  const noDefaultOptionConstraints = useAppSelector(
    selectAllNoDefaultOptionConstraints
  );
  const unmatchedCoupons = useAppSelector(selectAllUnmatchedCoupons);
  const transcriptMessages = useAppSelector(selectTranscriptMessages);

  // The previous hypothesis frame's timestamp bounds the attribution window in
  // resolveAnchor. Tracked by frame id so re-runs on transcript changes don't
  // advance it; reset per session so a stale window can't leak across cars.
  const sessionRef = useRef(currentSessionId);
  const frameIdRef = useRef<string | null>(null);
  const frameEpochRef = useRef<number | null>(null);
  const prevFrameEpochRef = useRef<number | null>(null);

  useEffect(() => {
    if (sessionRef.current !== currentSessionId) {
      sessionRef.current = currentSessionId;
      frameIdRef.current = null;
      frameEpochRef.current = null;
      prevFrameEpochRef.current = null;
    }
    if (!enabled) return;

    const frame: any = hypothesisFrame;
    if (frame && frame.id !== frameIdRef.current) {
      prevFrameEpochRef.current = frameEpochRef.current;
      const epoch = ISOStringToEpoch(frame.ws_timestamp ?? frame.timestamp);
      frameEpochRef.current = Number.isNaN(epoch) ? null : epoch;
      frameIdRef.current = frame.id;
    }

    const cardKeys = [
      ...notOnMenuCards.map(notOnMenuAnchorKey),
      ...invalidOptionLines.map(invalidOptionAnchorKey),
      ...quantityEscalationItems.map(quantityEscalationAnchorKey),
      ...noDefaultOptionConstraints.map(noDefaultOptionAnchorKey),
      ...unmatchedCoupons.map(unmatchedCouponAnchorKey),
    ];
    const missingKeys = cardKeys.filter((key) => !(key in anchors));
    if (missingKeys.length === 0) return;

    const anchor = resolveAnchor(
      collectCommittedCustomerLines(transcriptMessages),
      prevFrameEpochRef.current
    );
    dispatch(
      captureAnchors(
        Object.fromEntries(missingKeys.map((key) => [key, anchor]))
      )
    );
  }, [
    enabled,
    anchors,
    hypothesisFrame,
    currentSessionId,
    notOnMenuCards,
    invalidOptionLines,
    quantityEscalationItems,
    noDefaultOptionConstraints,
    unmatchedCoupons,
    transcriptMessages,
    dispatch,
  ]);
};

export { collectCommittedCustomerLines, resolveAnchor, EMPTY_ANCHOR };
export default useUnresolvedItemAnchors;
